import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme } from "next-themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { NumerolojiAdminMobileSidebar, NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { NumerolojiPackageEditor, type PackageDraft } from "@/components/admin/NumerolojiPackageEditor";
import { Button } from "@/components/ui/button";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";

import { Bell } from "lucide-react";

type CatalogPackage = {
  id: string;
  key: string;
  name: string;
  description: string;
  basePriceTry: number;
  discountedPriceTry?: number;
  active: boolean;
  requiresBirthDate: boolean;
  requiresPartner: boolean;
  imageUrl?: string;
};

type CatalogResponse = {
  ok: true;
  packages: CatalogPackage[];
  products: unknown[];
  templates: unknown[];
  settings: unknown;
};

async function fetchCatalog(): Promise<CatalogResponse> {
  const res = await adminFetch("/api/admin/numeroloji/catalog");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Katalog alınamadı.");
  }
  return json as CatalogResponse;
}

async function savePackage(payload: Partial<CatalogPackage> & { key: string; name: string }) {
  const res = await adminFetch("/api/admin/numeroloji/packages", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Paket kaydedilemedi.");
  }
  return json as { ok: true; item: CatalogPackage };
}

async function deletePackage(id: string) {
  const res = await adminFetch(`/api/admin/numeroloji/packages/${encodeURIComponent(id)}/delete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Paket silinemedi.");
  }
  return json as { ok: true; deleted: boolean };
}

function emptyDraft(): PackageDraft {
  return {
    key: "normal",
    name: "",
    description: "",
    basePriceTry: 0,
    discountedPriceTry: undefined,
    active: true,
    requiresBirthDate: true,
    requiresPartner: false,
  };
}

export default function AdminNumerolojiPackage() {
  const params = useParams();
  const packageId = params.id ? String(params.id).trim() : "";
  const isNew = !packageId;

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { theme, setTheme, systemTheme } = useTheme();
  const resolvedTheme = theme === "system" ? systemTheme : theme;
  const isDark = resolvedTheme === "dark";

  const { sessionQuery, loginMutation, logoutMutation } = useAdminSession();
  const configured = Boolean(sessionQuery.data?.configured);
  const authenticated = Boolean(sessionQuery.data?.authenticated);

  const catalogQuery = useQuery({
    queryKey: ["admin-numeroloji-catalog"],
    queryFn: fetchCatalog,
    enabled: authenticated,
    staleTime: 30_000,
  });

  const packages = useMemo(() => catalogQuery.data?.packages ?? [], [catalogQuery.data]);

  const [draft, setDraft] = useState<PackageDraft>(() => emptyDraft());
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    if (!authenticated) return;

    if (isNew) {
      if (!seeded) {
        setDraft(emptyDraft());
        setSeeded(true);
      }
      return;
    }

    if (!catalogQuery.data || seeded) return;

    const item = packages.find((p) => p.id === packageId);
    if (!item) {
      setSeeded(true);
      return;
    }

    setDraft({
      id: item.id,
      key: item.key,
      name: item.name,
      description: item.description,
      basePriceTry: item.basePriceTry,
      discountedPriceTry: item.discountedPriceTry,
      active: item.active,
      requiresBirthDate: item.requiresBirthDate,
      requiresPartner: item.requiresPartner,
      imageUrl: item.imageUrl,
    });
    setSeeded(true);
  }, [authenticated, catalogQuery.data, isNew, packageId, packages, seeded]);

  const saveMutation = useMutation({
    mutationFn: (payload: PackageDraft) => savePackage(payload as any),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-catalog"] });
      navigate("/admin/numeroloji/paket", { replace: true });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deletePackage(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-catalog"] });
      navigate("/admin/numeroloji/paket", { replace: true });
    },
  });

  if (sessionQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Yükleniyor...</div>;
  }

  if (!authenticated) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
      />
    );
  }

  if (!isNew && catalogQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Yükleniyor...</div>;
  }

  if (!isNew && catalogQuery.isError) {
    return <div className="text-sm text-destructive">{(catalogQuery.error as Error).message}</div>;
  }

  if (!isNew && catalogQuery.data && !packages.some((p) => p.id === packageId)) {
    return (
      <div className="p-4 sm:p-6">
        <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          Paket bulunamadı.
        </div>
        <div className="mt-4">
          <Button className="rounded-full" onClick={() => navigate("/admin/numeroloji/paket", { replace: true })}>
            Paket listesine dön
          </Button>
        </div>
      </div>
    );
  }

  const headerTitle = isNew ? "Yeni Paket" : "Paket Düzenle";

  return (
    <div className="flex min-h-dvh w-full">
      <NumerolojiAdminSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />

      <div className="flex min-w-0 flex-1 flex-col bg-slate-100/60 dark:bg-slate-900/30">
        <header className="h-16 border-b border-slate-200/70 bg-white/70 px-4 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/40 sm:px-6">
          <div className="flex h-full items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <NumerolojiAdminMobileSidebar
              mode="links"
              isDark={isDark}
              onToggleTheme={() => setTheme(isDark ? "light" : "dark")}
            />
              <div className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-50 sm:text-lg">
                {headerTitle}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300"
                aria-label="Bildirimler"
              >
                <Bell className="h-4 w-4" />
              </button>

              <Button
                variant="outline"
                className="rounded-full"
                size="sm"
                onClick={() => logoutMutation.mutate()}
                disabled={logoutMutation.isPending}
              >
                Çıkış
              </Button>
            </div>
          </div>
        </header>

        <div className="min-w-0 flex-1 p-4 sm:p-6">
          <NumerolojiPackageEditor
            draft={draft}
            setDraft={setDraft}
            isSaving={saveMutation.isPending}
            isDeleting={deleteMutation.isPending}
            onClose={() => navigate("/admin/numeroloji/paket", { replace: true })}
            onSave={(payload) => saveMutation.mutate(payload)}
            onDelete={
              isNew
                ? undefined
                : () => {
                    const ok = confirm("Bu paketi silmek istiyor musunuz?");
                    if (!ok) return;
                    deleteMutation.mutate(packageId);
                  }
            }
          />

          {saveMutation.isError ? (
            <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
              {(saveMutation.error as Error).message}
            </div>
          ) : null}

          {deleteMutation.isError ? (
            <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
              {(deleteMutation.error as Error).message}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
